import { BigNumber } from '@0x/utils'

BigNumber.config({
  EXPONENTIAL_AT: 100,
})

const formatWithSeparator = {
  decimalSeparator: '.',
  groupSeparator: ',',
  groupSize: 3,
}

/**
 * "Smart" format. Tries to use the best number of decimals depending on the number.
 * Optionally can show a '+' sign in front of positive numbers and format number with separators.
 * NOTE: use it ONLY TO DISPLAY values, NOT FOR INPUTS
 */
export function autoFormat(
  numbr: string | number | BigNumber,
  maxDecimals?: number,
  opts?: { sign?: boolean; noSeparator?: boolean; exponential?: boolean }
) {
  let dp = 2
  let num: BigNumber = !(numbr instanceof BigNumber) ? new BigNumber(numbr) : numbr
  if (num.isNaN()) {
    return '-'
  }
  if (num.isZero()) {
    return '0'
  }
  if (maxDecimals && num.lt(10 ** -maxDecimals)) {
    if (opts?.exponential) {
      return num.toExponential(2, 4)
    }
    return '0'
  }
  if (num.lt(1) && num.gt(-1)) {
    num = num.precision(3, 1)
    return num.toFixed()
  } else if (num.lt(100) && num.gt(-100)) {
    dp = 2
    num = num.decimalPlaces(dp)
  } else {
    dp = 0
    num = num.integerValue()
  }
  let str = opts?.noSeparator ? num.toFixed() : num.toFormat(dp, formatWithSeparator)
  if (str.includes('.')) {
    str = str.replace(/0+$/, '')
    if (str.endsWith('.')) {
      str = str.replace('.', '')
    }
  }
  return opts?.sign && num.gt(0) ? '+' + str : str
}

export function formatPercent(numbr: string | number | BigNumber) {
  let num: BigNumber = !(numbr instanceof BigNumber) ? new BigNumber(numbr) : numbr
  if (num.isNaN()) {
    return '-'
  }
  if (num.lt(0.001) && num.gt(-0.001)) {
    return '0'
  } else if (num.lt(0.1) && num.gt(-0.1)) {
    num = num.precision(1)
    return num.toFixed()
  } else if (num.lt(100) && num.gt(-100)) {
    num = num.precision(3)
    return num.toFixed()
  } else {
    return num.integerValue().toFixed()
  }
}

/**
 * This is a "smart rounding" function that always returns a number.
 * If no decimal places are passed, it will apply a smart rounding.
 *
 * * If number is very small (as in decimals), it will pick up to 4 most significant ones.
 * * If number is in [-1, 1], use most significant decimal numbers (up to 4).
 * * [1] note: in a list, we could have mixed values and asking for decimalPlaces 0,
 *       which is nice for big numbers but this throws an exception for the range [-1, 1].
 * For bigger ranges, use max 2 decimals by default
 * @param {Number} num number to round
 * @param {Number} decimalPlaces
 */
function round(num: number | BigNumber, decimalPlaces?: number) {
  let numbr = num instanceof BigNumber ? num : new BigNumber(num)

  if (numbr.isLessThan(0.0001) && numbr.isGreaterThan(-0.0001)) {
    const numbrString = numbr.toString().match(/[-]?0\.0+[1-9]{1,4}/)
    if (numbrString) {
      return Number(numbrString[0])
    }
    return 0
  } else if (numbr.isLessThan(1) && numbr.isGreaterThan(-1)) {
    numbr = numbr.precision(decimalPlaces || 3, 1) // precision can not be zero @see above [1]
  } else if (numbr.isLessThan(100000) && numbr.isGreaterThan(-100000)) {
    numbr = numbr.decimalPlaces(typeof decimalPlaces === 'number' ? decimalPlaces : 2, 1)
  } else {
    numbr = numbr.decimalPlaces(typeof decimalPlaces === 'number' ? decimalPlaces : 1, 1)
  }
  return numbr.toNumber()
}

/**
 * @param {number} num
 * @param {number} decimalPlaces default 2
 */
function roundWithSign(num: number | BigNumber | string, decimalPlaces = 2) {
  const numbr = num instanceof BigNumber ? num : new BigNumber(num)
  return numbr.gt(0) ? '+' + round(numbr, decimalPlaces) : round(numbr, decimalPlaces)
}

/**
 * This is a "rounding" function that always returns a string.
 *
 * @param {string | number | BigNumber} output number to round
 */

export function formatPrecision(output: string | number | BigNumber) {
  const outputNumber = new BigNumber(output)
  if (!outputNumber.isFinite() || outputNumber.isNaN()) {
    return outputNumber.toFixed()
  }
  const n = Math.log(Number(outputNumber.abs())) / Math.LN10
  const x = Math.round(4 - n)
  if (x < 2) {
    return outputNumber.dp(2, 1).toFixed()
  }
  if (x > 6) {
    return outputNumber.precision(3, 1).toFixed()
  }
  return outputNumber.dp(x, 1).toFixed()
}

/**
 * Abbreviate number using scale
 * 130000 -> 130K | 1300000 -> 1.3M
 */
export function abbreviate(val: string | number | BigNumber, maxDecimals = 6): string {
  const value = new BigNumber(val)
  if (value.isNaN()) {
    return '-'
  }
  if (value.isZero() || value.lt(10 ** -maxDecimals)) {
    return '0'
  }
  if (value.lt(1000)) {
    return value.precision(3, 1).toFixed()
  }
  if (value.lt(10 ** 6)) {
    return `${Number(value.dividedBy(1000).precision(3, 1)).toString()}K`
  }
  if (value.lt(10 ** 9)) {
    return `${Number(value.dividedBy(10 ** 6).precision(3, 1)).toString()}M`
  }
  return `${Number(value.dividedBy(10 ** 9).precision(3, 1)).toString()}B`
}

export default {
  autoFormat,
  abbreviate,
  formatPrecision,
  round,
  roundWithSign,
  formatPercent,
}
